import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { toNumberOrNull } from '@/lib/number';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { HarvestResponse } from '@/types/generated/openapi';

interface HarvestListProps {
  cycleId: string;
  farmId: string;
}

export function HarvestList({ cycleId, farmId }: HarvestListProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['harvests', cycleId, 'cycle-list'],
    queryFn: () =>
      api.get<{ data: HarvestResponse[] }>(`/api/v1/farms/${farmId}/harvests`, {
        params: { cycle_id: cycleId, limit: '100' },
      }),
  });

  const harvests = [...(data?.data ?? [])].sort((a, b) =>
    a.harvest_date.localeCompare(b.harvest_date)
  );
  if (isLoading || harvests.length === 0) return null;

  const totalKg = harvests.reduce((sum, h) => sum + (toNumberOrNull(h.total_weight_kg) ?? 0), 0);

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-rose-100 rounded-lg">
            <TrendingUp className="h-4 w-4 text-rose-600" />
          </div>
          Harvests
        </div>
      </Card.Header>
      <Card.Body className="space-y-3">
        {harvests.map((h) => {
          const kg = toNumberOrNull(h.total_weight_kg);
          const abw = toNumberOrNull(h.avg_weight_g);
          return (
            <div key={h.id} className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {format(parseISO(h.harvest_date), 'MMM d, yyyy')}
                </p>
                <p className="text-xs text-gray-400">
                  {h.piece_count ? `${h.piece_count.toLocaleString()} pcs` : 'pcs not recorded'}
                  {abw ? ` · ${abw.toFixed(1)} g ABW` : ''}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="text-sm font-semibold tabular-nums text-gray-900">
                  {kg != null ? `${kg.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg` : '—'}
                </span>
                <Badge variant={h.harvest_type === 'final' ? 'critical' : 'neutral'} size="sm">
                  {h.harvest_type === 'final' ? 'Final' : 'Partial'}
                </Badge>
              </div>
            </div>
          );
        })}
      </Card.Body>
      <Card.Footer>
        <p className="text-center text-xs text-gray-500">
          Total harvested: <span className="font-semibold tabular-nums text-gray-900">{totalKg.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg</span>
        </p>
      </Card.Footer>
    </Card>
  );
}
